import * as yup from 'yup'
import { useContent } from 'lib/content/react'
import {
  incentiveTypes,
  scheduleTypes,
  scheduleFrequencies,
  scheduleOccurrences,
  scheduleCustomOccurrences,
  scheduleEndings,
  IncentiveDetails,
  IncentiveType,
  ScheduleCustomOccurrence,
  ScheduleEnding,
  ScheduleFrequency,
  ScheduleOccurrence,
  ScheduleType,
} from 'types/entities'
import { CREDIT_TYPES } from './utils'

// Source: https://stackoverflow.com/a/11780743
// Minimum: 1.00
const currencyRegex = /^[1-9]\d*(((,\d{3}){1})?(\.\d{0,2})?)$/

// This is the type produced by casting
export type IncentiveDetailsSchema = yup.TypeOf<yup.SchemaOf<IncentiveDetails>>

type UseIncentiveDetailsModal = (
  incentiveDetails: IncentiveDetails,
  currencyCode: string,
  timezone: string
) => {
  initialValues: IncentiveDetailsSchema
  schema: yup.SchemaOf<IncentiveDetails>
}

export const useIncentiveDetailsModal: UseIncentiveDetailsModal = (
  incentiveDetails,
  currencyCode,
  timezone
) => {
  const { formatMessage } = useContent()
  const invalidInterval = formatMessage({
    id: 'pmportal.incentives.invalidInterval',
  })
  const invalidPercentage = formatMessage({
    id: 'pmportal.incentives.invalidPercentage',
  })
  const now = new Date()
  const currencyAmountSchema = yup
    .string()
    .matches(currencyRegex, {
      message: formatMessage({
        id: 'pmportal.incentives.invalidCurrencyAmount',
      }),
    })
    .defined()
    .default('')
  const optionalCurrencyAmountSchema = yup
    .string()
    .matches(currencyRegex, {
      excludeEmptyString: true,
      message: formatMessage({
        id: 'pmportal.incentives.invalidCurrencyAmount',
      }),
    })
    .defined()
    .default('')
  const schema = yup.object({
    name: yup.string().default(''),
    description: yup.string().default(''),
    type: yup
      .mixed<IncentiveType>()
      .defined()
      .oneOf(
        [...incentiveTypes],
        formatMessage({ id: 'pmportal.incentives.invalidType' })
      )
      // @ts-ignore: we want to initialize this as an invalid value, but TS complains about it
      .default('initial'),
    currencyCode: yup.string().defined().default(currencyCode),
    creditAmount: yup.string().when('type', {
      is: (type: IncentiveType) => CREDIT_TYPES.includes(type),
      then: currencyAmountSchema,
      otherwise: yup.string().defined().default(''),
    }),
    discountAmount: yup.string().when('type', {
      is: 'discountAmount',
      then: currencyAmountSchema,
      otherwise: yup.string().defined().default(''),
    }),
    discountPercentage: yup.number().when('type', {
      is: 'discountPercentage',
      then: yup
        .number()
        .typeError(invalidPercentage)
        .integer(invalidPercentage)
        .min(1, invalidPercentage)
        .max(100, invalidPercentage)
        .defined()
        .default(0),
      otherwise: yup.number().defined().default(0),
    }),
    discountValueMax: optionalCurrencyAmountSchema,
    orderValueMin: optionalCurrencyAmountSchema,
    schedule: yup
      .object({
        type: yup
          .mixed<ScheduleType>()
          .defined()
          .oneOf([...scheduleTypes])
          .default('none'),
        start: yup.date().default(now),
        timezone: yup.string().default(timezone),
        fromTime: yup.string().default('09:00'),
        toTime: yup.string().default('17:00'),
        interval: yup
          .number()
          .typeError(invalidInterval)
          .integer(invalidInterval)
          .min(1, invalidInterval)
          .default(2),
        customFrequency: yup
          .mixed<ScheduleFrequency>()
          .oneOf([...scheduleFrequencies])
          .default('daily'),
        days: yup.array().of(yup.number()).default([1, 2, 3, 4, 5]),
        weekStart: yup.number().integer().default(1),
        weekRange: yup.number().integer().default(5),
        monthlyOccurrence: yup
          .mixed<ScheduleOccurrence>()
          .oneOf([...scheduleOccurrences])
          .default('first'),
        customOccurrence: yup
          .mixed<ScheduleCustomOccurrence>()
          .oneOf([...scheduleCustomOccurrences])
          .default('sameMonthDay'),
        ending: yup
          .mixed<ScheduleEnding>()
          .oneOf([...scheduleEndings])
          .default('never'),
        endingOn: yup.date().default(now),
      })
      .defined()
      .default({}),
  })
  return { initialValues: schema.cast(incentiveDetails), schema }
}
